import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Fab,
  Tooltip,
  CircularProgress,
  Paper,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";

import { useInventory } from "./hooks/useInventory";
import { useTechnicians } from "../Tecnicos/hooks/useTechnicians";
import { useFilters } from "./hooks/useFilters";
import { useSnackbar } from "./hooks/useSnackbar";
import { PRODUCT_FIELDS } from "./constants";
import { parseError } from "./utils";
import locationsService from "../../api/locationsService";
import inventoryService from "../../api/inventoryService";

import InventoryHeader from "./components/InventoryHeader";
import InventoryFilters from "./components/InventoryFilters";
import ProductListView from "./components/ProductListView";
import StockDistributionPanel from "./components/StockDistributionPanel";
import TransferStockModal from "./components/TransferStockModal";
import ConsumeStockModal from "./components/ConsumeStockModal";
import ReceiveStockModal from "./components/ReceiveStockModal";
import MovementsHistory from "./components/MovementsHistory";
import LocationsModal from "./components/LocationsModal";
import MinStockModal from "./components/MinStockModal";
import ModalFormUniversal from "../../components/ModalFormUniversal";
import ModalConfirmUniversal from "../../components/ModalConfirmUniversal";

export default function Inventario() {
  // Estados principales
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [modalCreate, setModalCreate] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [modalTransfer, setModalTransfer] = useState(false);
  const [modalConsume, setModalConsume] = useState(false);
  const [modalReceive, setModalReceive] = useState(false);
  const [modalLocations, setModalLocations] = useState(false);
  const [modalMinStock, setModalMinStock] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  // Custom Hooks
  const {
    products,
    selected,
    setSelected,
    createProduct,
    updateProduct,
    deleteProduct,
    transferStock,
    consumeStock,
    receiveStock,
    loadProducts,
    loading: loadingProducts,
  } = useInventory();

  const {
    technicians,
    loading: loadingTechnicians,
  } = useTechnicians();

  const {
    filterCategory,
    setFilterCategory,
    search,
    setSearch,
    filtered,
  } = useFilters(products);

  const { snackOpen, snackMsg, snackSeverity, showSnackbar, closeSnackbar } =
    useSnackbar();

  // Cargar ubicaciones al montar el componente
  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const response = await locationsService.getAllLocations();
      const locationData = response.data || [];
      setLocations(locationData);
      
      // Seleccionar el primer almacén activo por defecto
      if (!selectedWarehouse) {
        const activeWarehouses = locationData.filter(loc => loc.type === "main_warehouse" && loc.status === "active");
        if (activeWarehouses.length > 0) {
          setSelectedWarehouse(activeWarehouses[0]);
        }
      }
    } catch (error) {
      console.error("Error cargando ubicaciones:", error);
    }
  };

  // Extraer categorías únicas del inventario para sugerencias
  const getUniqueCategories = () => {
    const categories = products
      .map(p => p.category)
      .filter(cat => cat && cat.trim() !== "");
    return [...new Set(categories)].sort();
  };

  // Crear campos dinámicos con categorías sugeridas
  const getProductFieldsWithCategories = () => {
    const categories = getUniqueCategories();
    return PRODUCT_FIELDS.map(field => {
      if (field.name === "category") {
        return {
          ...field,
          options: categories
        };
      }
      return field;
    });
  };

  // Handlers
  const handleCreateOpen = () => {
    setModalCreate(true);
  };

  const handleCreateSubmit = async (data) => {
    try {
      await createProduct(data);
      setModalCreate(false);
      showSnackbar("✅ Artículo creado correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleSelect = (product) => {
    setSelected(product);
    // No abrir automáticamente el panel - el usuario lo controla manualmente
  };

  const handleEditOpen = (product) => {
    setSelected(product);
    setModalEdit(true);
  };

  const handleEditSubmit = async (data) => {
    try {
      await updateProduct(selected._id, data);
      setModalEdit(false);
      setSelected(null);
      showSnackbar("✅ Artículo actualizado correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleDeleteOpen = (product) => {
    setSelected(product);
    setModalDelete(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteProduct(selected._id);
      setModalDelete(false);
      setSelected(null);
      showSnackbar("✅ Artículo eliminado correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleTransferOpen = (product) => {
    setSelected(product);
    setModalTransfer(true);
  };

  const handleTransferSubmit = async (transferData) => {
    try {
      await transferStock(transferData);
      setModalTransfer(false);
      setSelected(null);
      showSnackbar("✅ Transferencia realizada correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleConsumeOpen = (product) => {
    setSelected(product);
    setModalConsume(true);
  };

  const handleConsumeSubmit = async (consumeData) => {
    try {
      await consumeStock(consumeData);
      setModalConsume(false);
      setSelected(null);
      showSnackbar("✅ Consumo registrado correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleReceiveOpen = (product) => {
    setSelected(product);
    setModalReceive(true);
  };

  const handleReceiveSubmit = async (receiveData) => {
    try {
      await receiveStock(receiveData);
      setModalReceive(false);
      setSelected(null);
      showSnackbar("✅ Stock recibido correctamente", "success");
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  const handleEditMinStockOpen = (location, type) => {
    setSelectedLocation({ ...location, locationType: type });
    setModalMinStock(true);
  };

  const handleEditMinStockSubmit = async (data) => {
    try {
      await inventoryService.updateMinStock(data.itemId, data.locationId, data.minStock);
      showSnackbar("✅ Stock mínimo actualizado correctamente", "success");
      //REINICIAR DATA AQUI
      await loadProducts();
      setModalMinStock(false);
      setSelected(null);
    } catch (err) {
      showSnackbar(parseError(err), "error");
    }
  };

  // Filtrar productos según el tab activo
  const getFilteredByTab = () => {
    if (currentTab === 0) {
      // Tab 0: Almacenes - Mostrar inventario del almacén seleccionado
      if (!selectedWarehouse) return [];
      
      return filtered
        .map(product => {
          // Buscar el stock del almacén seleccionado
          const warehouseStock = product.warehouses?.find(
            w => (w.locationId?.toString() || w.locationId) === selectedWarehouse._id
          );
          
          if (!warehouseStock) return null;
          
          // Crear una versión del producto indicando el almacén seleccionado
          return {
            ...product,
            warehouseName: selectedWarehouse.name,
            selectedLocationId: selectedWarehouse._id,
          };
        })
        .filter(Boolean);
    } else if (currentTab === 1) {
      // Tab 1: Técnicos - Mostrar inventario del técnico seleccionado
      if (!selectedTechnician) return [];
      
      return filtered
        .map(product => {
          // Buscar el stock del técnico seleccionado
          const techStock = product.technicians?.find(
            t => t.technicianId === selectedTechnician._id
          );
          
          if (!techStock) return null;
          
          // Crear una versión del producto con el stock del técnico
          return {
            ...product,
            technicianStock: techStock.stock,
            technicianName: selectedTechnician.name,
          };
        })
        .filter(Boolean);
    } else if (currentTab === 2) {
      // Tab 2: Residuos Peligrosos - Filtrar solo productos con residuos
      return filtered.filter(p => (p.hazardousStock || 0) > 0);
    }
    
    return filtered;
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSelected(null);
    
    // Si cambia a tab de almacenes y no hay almacén seleccionado, seleccionar el primero activo
    if (newValue === 0 && !selectedWarehouse) {
      const activeWarehouses = locations.filter(loc => loc.type === "main_warehouse" && loc.status === "active");
      if (activeWarehouses.length > 0) {
        setSelectedWarehouse(activeWarehouses[0]);
      }
    }
    
    // Si cambia a tab de técnicos y no hay técnico seleccionado, seleccionar el primero activo
    if (newValue === 1 && !selectedTechnician) {
      const activeTechs = technicians.filter(t => t.status === "active");
      if (activeTechs.length > 0) {
        setSelectedTechnician(activeTechs[0]);
      }
    }
  };

  const getTabLabel = (index) => {
    if (index === 0 && selectedWarehouse) {
      return selectedWarehouse.name || "Almacén";
    }
    if (index === 1 && selectedTechnician) {
      return selectedTechnician.name;
    }
    const labels = ["Almacenes", "Técnicos", "Residuos Peligrosos", "Historial"];
    return labels[index];
  };

  const getTabIcon = (index) => {
    const icons = [
      <WarehouseIcon key="warehouse" />,
      <EngineeringIcon key="tech" />,
      <DeleteForeverIcon key="hazard" />,
      <HistoryIcon key="history" />,
    ];
    return icons[index];
  };

  // Filtrar solo técnicos activos y almacenes activos
  const activeTechnicians = technicians.filter(t => t.status === "active");
  const activeWarehouses = locations.filter(loc => loc.type === "main_warehouse" && loc.status === "active");

  // Loading state
  if (loadingProducts || loadingTechnicians) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#fafafa",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Cargando inventario...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fafafa",
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 4,
          pt: 3,
          pb: 2,
        }}
      >
        <InventoryHeader onManageLocations={() => setModalLocations(true)} />

        {/* TABS */}
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            mt: 2,
            "& .MuiTab-root": {
              minHeight: 56,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            },
          }}
        >
          {[0, 1, 2, 3].map((index) => (
            <Tab
              key={index}
              icon={getTabIcon(index)}
              label={getTabLabel(index)}
              iconPosition="start"
              sx={{
                "&.Mui-selected": {
                  color: "primary.main",
                },
              }}
            />
          ))}
        </Tabs>

        {/* SELECTOR DE ALMACÉN (solo visible en tab de almacenes) */}
        {currentTab === 0 && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {activeWarehouses.length > 0 ? (
              activeWarehouses.map((warehouse) => (
                <Tooltip key={warehouse._id} title={`Ver inventario de ${warehouse.name}`}>
                  <Box
                    onClick={() => setSelectedWarehouse(warehouse)}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: selectedWarehouse?._id === warehouse._id 
                        ? "primary.main" 
                        : "divider",
                      bgcolor: selectedWarehouse?._id === warehouse._id 
                        ? "primary.50" 
                        : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.50",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarehouseIcon 
                        fontSize="small"
                        sx={{
                          color: selectedWarehouse?._id === warehouse._id 
                            ? "primary.main" 
                            : "text.secondary"
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={selectedWarehouse?._id === warehouse._id ? 700 : 600}
                        color={selectedWarehouse?._id === warehouse._id 
                          ? "primary.main" 
                          : "text.primary"}
                      >
                        {warehouse.name || "Almacén"}
                      </Typography>
                    </Stack>
                  </Box>
                </Tooltip>
              ))
            ) : (
              <Box sx={{ py: 2, px: 3, bgcolor: "grey.100", borderRadius: 2, width: "100%" }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No hay almacenes disponibles. Crea almacenes desde "Gestionar Almacenes".
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* SELECTOR DE TÉCNICO (solo visible en tab de técnicos) */}
        {currentTab === 1 && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {activeTechnicians.length > 0 ? (
              activeTechnicians.map((tech) => (
                <Tooltip key={tech._id} title={`Ver inventario de ${tech.name}`}>
                  <Box
                    onClick={() => setSelectedTechnician(tech)}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor: selectedTechnician?._id === tech._id 
                        ? "primary.main" 
                        : "divider",
                      bgcolor: selectedTechnician?._id === tech._id 
                        ? "primary.50" 
                        : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "primary.50",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EngineeringIcon 
                        fontSize="small"
                        sx={{
                          color: selectedTechnician?._id === tech._id 
                            ? "primary.main" 
                            : "text.secondary"
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={selectedTechnician?._id === tech._id ? 700 : 600}
                        color={selectedTechnician?._id === tech._id 
                          ? "primary.main" 
                          : "text.primary"}
                      >
                        {tech.name}
                      </Typography>
                    </Stack>
                  </Box>
                </Tooltip>
              ))
            ) : (
              <Box sx={{ py: 2, px: 3, bgcolor: "grey.100", borderRadius: 2, width: "100%" }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No hay técnicos activos. Agrega técnicos desde el módulo de Técnicos.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* FILTROS (solo mostrar en tabs de inventario, no en historial) */}
      {currentTab !== 3 && (
        <Box sx={{ bgcolor: "white", px: 4, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <InventoryFilters
            products={products}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            search={search}
            setSearch={setSearch}
          />
        </Box>
      )}

      {/* MAIN CONTENT */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          p: 3,
        }}
      >
        {currentTab === 3 ? (
          /* TAB DE HISTORIAL */
          <Box sx={{ height: "100%" }}>
            <MovementsHistory />
          </Box>
        ) : (
          <>
            {/* LISTA DE PRODUCTOS */}
            <Box
              sx={{
                height: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(0,0,0,0.2)",
                  borderRadius: "4px",
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.3)",
                  },
                },
              }}
            >
            <ProductListView
              products={getFilteredByTab()}
              selected={selected}
              onSelect={handleSelect}
              onEdit={handleEditOpen}
              onDelete={handleDeleteOpen}
              onTransfer={handleTransferOpen}
              currentTab={currentTab}
              selectedTechnician={selectedTechnician}
              selectedWarehouse={selectedWarehouse}
            />
          </Box>

          {/* PANEL FLOTANTE DERECHO: DISTRIBUCIÓN Y DETALLES */}
          {selected && showDetailsPanel && (
            <Box
              sx={{
                position: "fixed",
                top: 80,
                right: 24,
                bottom: 24,
                width: 500,
                zIndex: 1200,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  height: "100%",
                  overflow: "auto",
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "rgba(0,0,0,0.15)",
                    borderRadius: "3px",
                    "&:hover": {
                      bgcolor: "rgba(0,0,0,0.25)",
                    },
                  },
                }}
              >
                {/* Botón para cerrar el panel */}
                <Box
                  sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    p: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>
                      Detalles del Producto
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => setShowDetailsPanel(false)}
                      sx={{ color: "text.secondary" }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={{ p: 2, flex: 1 }}>
                  <StockDistributionPanel
                    product={selected}
                    onEdit={handleEditOpen}
                    onTransfer={handleTransferOpen}
                    onConsume={handleConsumeOpen}
                    onReceive={handleReceiveOpen}
                    onEditMinStock={handleEditMinStockOpen}
                    currentTab={currentTab}
                    selectedTechnician={selectedTechnician}
                    selectedWarehouse={selectedWarehouse}
                    technicians={activeTechnicians}
                  />
                </Box>
              </Paper>
            </Box>
          )}
          </>
        )}
      </Box>

      {/* FAB: Crear Nuevo Artículo */}
      <Tooltip title="Crear nuevo artículo" placement="left">
        <Fab
          color="primary"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 64,
            height: 64,
          }}
          onClick={handleCreateOpen}
        >
          <AddIcon sx={{ fontSize: 32 }} />
        </Fab>
      </Tooltip>

      {/* FAB: Mostrar/Ocultar Panel de Detalles */}
      {selected && (
        <Tooltip 
          title={showDetailsPanel ? "Ocultar detalles" : "Mostrar detalles del producto"} 
          placement="left"
        >
          <Fab
            color={showDetailsPanel ? "secondary" : "info"}
            sx={{
              position: "fixed",
              bottom: 104,
              right: 24,
              width: 56,
              height: 56,
            }}
            onClick={() => setShowDetailsPanel(!showDetailsPanel)}
          >
            {showDetailsPanel ? <CloseIcon /> : <InfoIcon />}
          </Fab>
        </Tooltip>
      )}

      {/* MODALES */}
      <ModalFormUniversal
        open={modalCreate}
        onClose={() => setModalCreate(false)}
        title="Nuevo artículo"
        fields={getProductFieldsWithCategories()}
        initialData={{}}
        onSubmit={handleCreateSubmit}
      />

      <ModalFormUniversal
        open={modalEdit}
        onClose={() => {
          setModalEdit(false);
          setSelected(null);
        }}
        title={`Editar: ${selected?.name || ""}`}
        fields={getProductFieldsWithCategories()}
        initialData={selected || {}}
        onSubmit={handleEditSubmit}
      />

      <ModalConfirmUniversal
        open={modalDelete}
        onClose={() => {
          setModalDelete(false);
          setSelected(null);
        }}
        title="Eliminar artículo"
        message={`¿Estás seguro de eliminar "${selected?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        color="error"
        onConfirm={handleDeleteConfirm}
      />

      <TransferStockModal
        open={modalTransfer}
        onClose={() => {
          setModalTransfer(false);
          setSelected(null);
        }}
        product={selected}
        technicians={activeTechnicians}
        locations={locations}
        onSubmit={handleTransferSubmit}
      />

      <ConsumeStockModal
        open={modalConsume}
        onClose={() => {
          setModalConsume(false);
          setSelected(null);
        }}
        product={selected}
        technicians={activeTechnicians}
        locations={locations}
        selectedTechnician={currentTab === 1 ? selectedTechnician : null}
        selectedWarehouse={currentTab === 0 ? selectedWarehouse : null}
        onSubmit={handleConsumeSubmit}
      />

      <ReceiveStockModal
        open={modalReceive}
        onClose={() => {
          setModalReceive(false);
          setSelected(null);
        }}
        product={selected}
        technicians={activeTechnicians}
        locations={locations}
        onSubmit={handleReceiveSubmit}
      />

      <LocationsModal
        open={modalLocations}
        onClose={() => setModalLocations(false)}
        onSuccess={() => {
          loadLocations(); // Recargar ubicaciones cuando se crean/eliminan
          showSnackbar("✅ Cambios guardados correctamente", "success");
        }}
      />

      <MinStockModal
        open={modalMinStock}
        onClose={() => {
          setModalMinStock(false);
          setSelectedLocation(null);
        }}
        product={selected}
        location={selectedLocation}
        onSubmit={handleEditMinStockSubmit}
      />

      {/* SNACKBAR */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackSeverity}
          variant="filled"
          elevation={6}
          onClose={closeSnackbar}
          sx={{ minWidth: 300 }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}